import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import { LEGACY_LANE_SOURCES, OPT_IN_SOURCES, SOURCE_KEYS } from './chatSources'
import type { SourceKey, SourceSelection } from './chatSources'
import { isCantonCode } from '../shared/cantons'

/** One checkbox per search source of the chat (server src/agents/sources.py). */
export type ChatContextSettings = SourceSelection
export type ChatContextKey = SourceKey

interface ChatContextType {
  contextSettings: ChatContextSettings
  updateContextSetting: (key: ChatContextKey) => void
  /** Select (true) or clear (false) several sources at once: a group of the selector. */
  setSources: (keys: ReadonlyArray<ChatContextKey>, value: boolean) => void
  /** Cantons of the cantonal-law source chosen in this browser; null = the firm's (subproject 3e). */
  cantons: string[] | null
  setCantons: (codes: string[] | null) => void
}

export const CONTEXT_STORAGE_KEY = 'chat_sources_v4'
// Written for one day with the federal courts on by default: translated into v4 once, then removed.
export const LEGACY_V3_STORAGE_KEY = 'chat_sources_v3'
// Lane checkboxes saved before subproject 3c: translated into sources once, then removed.
export const LEGACY_V2_STORAGE_KEY = 'chat_context_settings_v2'
// Settings of an older lane set: ignored and removed.
const LEGACY_STORAGE_KEY = 'chat_context_settings'

// The user's own choice of cantons (absent = the firm's cantons, from the server)
export const CANTONS_STORAGE_KEY = 'chat_cantons_v1'

const parseCantons = (raw: string | null): string[] | null => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    const codes = Array.isArray(parsed) ? Array.from(new Set(parsed.filter(isCantonCode))) : []
    return codes.length ? codes : null
  } catch {
    return null
  }
}

const readStoredCantons = (): string[] | null => {
  try {
    return parseCantons(localStorage.getItem(CANTONS_STORAGE_KEY))
  } catch {
    return null
  }
}

const writeStoredCantons = (cantons: string[] | null) => {
  try {
    if (cantons) localStorage.setItem(CANTONS_STORAGE_KEY, JSON.stringify(cantons))
    else localStorage.removeItem(CANTONS_STORAGE_KEY)
  } catch {
    /* storage unavailable: the choice lasts for this session only */
  }
}

// Every source is searched by default, except the opt-in ones (federal courts).
export const DEFAULT_CONTEXT_SETTINGS: ChatContextSettings =
  Object.fromEntries(SOURCE_KEYS.map((key) => [key, !OPT_IN_SOURCES.includes(key)])) as ChatContextSettings

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

/** Known sources with a boolean value survive; everything else takes the default. */
const normalizeSettings = (value: unknown): ChatContextSettings => {
  const stored = asRecord(value)
  const settings = { ...DEFAULT_CONTEXT_SETTINGS }
  for (const key of SOURCE_KEYS) {
    const flag = stored[key]
    if (typeof flag === 'boolean') settings[key] = flag
  }
  return settings
}

/** The v2 lane checkboxes as sources: a lane's value goes to each of its sources. */
const fromLegacyLanes = (value: unknown): ChatContextSettings => {
  const stored = asRecord(value)
  const settings = { ...DEFAULT_CONTEXT_SETTINGS }
  for (const [lane, keys] of Object.entries(LEGACY_LANE_SOURCES)) {
    const flag = stored[lane]
    if (typeof flag === 'boolean') keys.forEach((key) => { settings[key] = flag })
  }
  return settings
}

/** A v3 value: its choices survive, but the opt-in sources go back to off. */
const fromV3 = (value: unknown): ChatContextSettings => {
  const settings = normalizeSettings(value)
  OPT_IN_SOURCES.forEach((key) => { settings[key] = false })
  return settings
}

const sameSettings = (a: ChatContextSettings, b: ChatContextSettings) =>
  SOURCE_KEYS.every((key) => a[key] === b[key])

const writeStoredSettings = (settings: ChatContextSettings) => {
  try {
    localStorage.setItem(CONTEXT_STORAGE_KEY, JSON.stringify(settings))
  } catch {
    /* storage unavailable: the settings last for this session only */
  }
}

const removeStored = (key: string) => {
  try {
    localStorage.removeItem(key)
  } catch {
    /* storage unavailable */
  }
}

// Storage may be unavailable (private mode, blocked site data): every access
// is guarded and the defaults apply.
const readStoredSettings = (): ChatContextSettings => {
  removeStored(LEGACY_STORAGE_KEY)
  try {
    const raw = localStorage.getItem(CONTEXT_STORAGE_KEY)
    // v3 is removed even when v4 exists: a tab still on the previous build may
    // have written it again after the translation
    const v3 = localStorage.getItem(LEGACY_V3_STORAGE_KEY)
    if (v3 !== null) removeStored(LEGACY_V3_STORAGE_KEY)
    if (raw) return normalizeSettings(JSON.parse(raw))
    if (v3) {
      const settings = fromV3(JSON.parse(v3))
      writeStoredSettings(settings)
      return settings
    }
    const legacy = localStorage.getItem(LEGACY_V2_STORAGE_KEY)
    if (legacy) {
      removeStored(LEGACY_V2_STORAGE_KEY)
      return fromLegacyLanes(JSON.parse(legacy))
    }
  } catch {
    /* storage unavailable or malformed value: the defaults */
  }
  return { ...DEFAULT_CONTEXT_SETTINGS }
}

const ChatContext = createContext<ChatContextType>({
  contextSettings: DEFAULT_CONTEXT_SETTINGS,
  updateContextSetting: () => {},
  setSources: () => {},
  cantons: null,
  setCantons: () => {},
})

export const useChatContext = () => useContext(ChatContext)

export const ChatContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [contextSettings, setContextSettings] = useState<ChatContextSettings>(readStoredSettings)
  const [cantons, setCantonsState] = useState<string[] | null>(readStoredCantons)

  useEffect(() => {
    writeStoredCantons(cantons)
  }, [cantons])

  useEffect(() => {
    writeStoredSettings(contextSettings)
  }, [contextSettings])

  // Keep the tabs in sync
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === CANTONS_STORAGE_KEY) {
        setCantonsState(parseCantons(event.newValue))
        return
      }
      if (event.key !== CONTEXT_STORAGE_KEY || !event.newValue) return
      try {
        const next = normalizeSettings(JSON.parse(event.newValue))
        setContextSettings((prev) => (sameSettings(prev, next) ? prev : next))
      } catch {
        /* malformed value written by another tab: ignored */
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const updateContextSetting = useCallback((key: ChatContextKey) => {
    setContextSettings((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const setSources = useCallback((keys: ReadonlyArray<ChatContextKey>, value: boolean) => {
    setContextSettings((prev) => {
      const next = { ...prev }
      keys.forEach((key) => { next[key] = value })
      return next
    })
  }, [])

  const setCantons = useCallback((codes: string[] | null) => {
    const valid = codes ? Array.from(new Set(codes.filter(isCantonCode))) : []
    setCantonsState(valid.length ? valid : null)
  }, [])

  const value = useMemo(() => ({ contextSettings, updateContextSetting, setSources, cantons, setCantons }),
                        [contextSettings, updateContextSetting, setSources, cantons, setCantons])

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}
